import PropTypes from 'prop-types';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
  Line
} from 'recharts';
import '../styles/companionAppUsageReport.css';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';

const DAU_COLOR = '#e0473f';
const MAU_COLOR = '#f2994a';

const formatDate = (date) => {
  if (!date) return '-';
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return '-';

  return parsedDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

const LEGEND_ITEMS = [
  { key: 'dauCurrent', type: 'box', dashed: false, color: DAU_COLOR },
  { key: 'mauCurrent', type: 'box', dashed: false, color: MAU_COLOR },
  { key: 'dauPrevious', type: 'box', dashed: true, color: DAU_COLOR },
  { key: 'mauPrevious', type: 'box', dashed: true, color: MAU_COLOR },
];

const renderLegend = ({ payload }) => {
  const labelByKey = payload.reduce((acc, entry) => {
    acc[entry.dataKey] = entry.value;
    return acc;
  }, {});

  const visibleLegendItems = LEGEND_ITEMS.filter(({ key }) => key in labelByKey);

  return (
    <div className="active-user-trend__legend">
      {visibleLegendItems.map(({ key, type, dashed, color }) => (
        <div
          key={key}
          className="active-user-trend__legend-item"
        >
          <svg width={22} height={14} style={{ overflow: 'visible' }}>
            {type === 'line' ? (
              <line
                x1={0}
                y1={7}
                x2={22}
                y2={7}
                stroke={color}
                strokeWidth={2}
                strokeDasharray={dashed ? '4 3' : undefined}
              />
            ) : (
              <rect
                x={0}
                y={1}
                width={22}
                height={12}
                rx={2}
                fill={dashed ? 'transparent' : `${color}33`}
                stroke={color}
                strokeWidth={1.5}
                strokeDasharray={dashed ? '4 3' : undefined}
              />
            )}
          </svg>
          <span style={{ fontSize: 12, color: '#333' }}>
            {labelByKey[key]}
          </span>
        </div>
      ))}
    </div>
  );
};

const renderTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const labels = {
    dauCurrent: 'DAU (Current Year)',
    dauPrevious: 'DAU (Previous Year)',
    mauCurrent: 'MAU (Current Year)',
    mauPrevious: 'MAU (Previous Year)',
  };

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e5e5',
        borderRadius: 6,
        padding: '8px 12px',
        fontSize: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        {formatDate(label)}
      </div>
      {payload.map((entry) => (
        <div key={entry.dataKey}>
          {labels[entry.dataKey] ?? entry.dataKey}: {entry.value}
        </div>
      ))}
    </div>
  );
};

const ActiveUserTrends = ({ activeUserTrends, isLoading = false, yearOverYearComparison = true }) => {

  if (isLoading) {
    return (
      <Row className="g-3 mb-3">
        <Col xs={12}>
          <div className="companion-report-card page-break-avoid">
            <div className="companion-card-title-row">
              <div className="d-flex align-items-center">
                <Skeleton width={280} height={20} />
              </div>
              <Skeleton width={220} height={16} />
            </div>

            <div className="active-user-trend__chart active-user-trend__chart--skeleton">
              <Skeleton height={400} />

              <div className="active-user-trend__legend">
                {[1, 2, 3, 4].map((item) => (
                  <div
                    key={item}
                    className="active-user-trend__legend-item"
                  >
                    <Skeleton width={28} height={14} />
                    <Skeleton width={70} height={14} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    );
  }

  if (!activeUserTrends) {
    return null;
  }

  const helpText = (
    <>
      <h6>Active User Trend</h6>
      <span className="mb-0">
        DAU and MAU plotted per day. Solid = current year, dashed = same dates last
        year. Y-axis = unique active users.
      </span>
    </>
  );

  const dailyData = activeUserTrends.dailyActiveUsers ?? [];
  const monthlyData = activeUserTrends.monthlyActiveUsers ?? [];
  const currentYear = dailyData.length
    ? new Date(dailyData[0].date).getFullYear()
    : new Date().getFullYear();

  const previousYear = currentYear - 1;

  const data = dailyData.map((dailyItem, index) => ({
    date: dailyItem.date,

    dauCurrent: dailyItem.currentYearValue ?? 0,
    dauPrevious: dailyItem.previousYearValue ?? 0,

    mauCurrent: monthlyData[index]?.currentYearValue ?? 0,
    mauPrevious: monthlyData[index]?.previousYearValue ?? 0,
  }));

  const header = (
    <div className="companion-card-title-row">
      <div className='d-flex align-items-center'>
        <h2 className="companion-card-title">Daily & Monthly Active Users Trend</h2>
        {helpText && (
          <OverlayTrigger
            key='activeUserTrend'
            placement="top"
            overlay={<Tooltip id="active-user-trend-tooltip" className="custom-tooltip"> {helpText} </Tooltip>}
          >
            <FontAwesomeIcon className="circle-question mx-1" icon={faCircleQuestion} />
          </OverlayTrigger>
        )}
      </div>
      <span className="active-user-trend__description">
        Solid: Current Year{yearOverYearComparison && ' | Dashed: Previous Year'}
      </span>
    </div>
  );

  if (!data.length) {
    return (
      <div className="companion-report-card page-break-avoid">
        {header}
        <div className="companion-empty-state active-user-trend__empty">
          No active user trend data available
        </div>
      </div>
    );
  }

  return (
    <div className="companion-report-card page-break-avoid">
      {header}

      <div className="active-user-trend__chart">
        <ResponsiveContainer height={400}>
          <AreaChart
            data={data}
            margin={{
              top: 10,
              right: 50,
              left: 10,
              bottom: 10,
            }}
          >
            <defs>
              <linearGradient id="activeUsersGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={MAU_COLOR} stopOpacity={0.2} />
                <stop offset="100%" stopColor={MAU_COLOR} stopOpacity={0.02} />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#eee"
            />

            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 12, fill: '#666' }}
              axisLine={false}
              tickLine={false}
            />

            <YAxis
              tick={{ fontSize: 12, fill: '#666' }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              domain={[
                (dataMin) => Math.max(0, Math.floor(dataMin * 0.9)),
                (dataMax) => Math.ceil(dataMax * 1.1),
              ]}
              tickCount={8}
            />

            <ChartTooltip content={renderTooltip} />

            <Legend content={renderLegend} verticalAlign="bottom" />

            <Area
              type="monotone"
              dataKey="mauCurrent"
              name={`MAU (${currentYear})`}
              stroke={MAU_COLOR}
              fill="url(#activeUsersGradient)"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />

            <Line
              type="monotone"
              dataKey="dauCurrent"
              name={`DAU (${currentYear})`}
              stroke={DAU_COLOR}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />

            {yearOverYearComparison && (
              <Line
                type="monotone"
                dataKey="dauPrevious"
                name={`DAU (${previousYear})`}
                stroke={DAU_COLOR}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                activeDot={{ r: 4 }}
              />
            )}

            {yearOverYearComparison && (
              <Line
                type="monotone"
                dataKey="mauPrevious"
                name={`MAU (${previousYear})`}
                stroke={MAU_COLOR}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                activeDot={{ r: 4 }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ActiveUserTrends;

ActiveUserTrends.propTypes = {
  activeUserTrends: PropTypes.object,
  isLoading: PropTypes.bool,
  yearOverYearComparison: PropTypes.bool
};